import { PRIMARY_SOURCE, SCHEMA_VERSION, ScreenTileSchema, tileOptionsJsonSchema, type Dashboard, type DataSource, type ScreenTile, type TileStyle } from './dashboard.ts';
import { migrateDashboard, parseDashboard } from './parse.ts';

// Writing an edited dashboard back into its file (requirement S-4 in docs/specs/dashboard-editor.md).
// The editor works on the parsed dashboard, in which every tile has an id and the short forms are
// folded out (`dataSource`, the default `options.source`). Writing that would lose the short forms
// and fill the file with defaults, so the edit is applied to the file's own JSON instead: each tile
// keeps what the file said, and gets the id and place the editor gave it.

type Json = Record<string, unknown>;

/**
 * Where a tile the file does not have comes from: a copy of a tile (by id), written as the file has
 * that one; or a new tile, written as given (the short form the editor made it in).
 */
export type TileOrigin = { readonly copyOf: string } | { readonly json: Readonly<Json> };

function isRecord(value: unknown) : value is Json {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The file's tiles by the id the parser gave them. The parsed screen has the file's tiles in the
 * file's order, each group followed by the tiles it held in the file (read onto the screen).
 */
function fileTilesById(file: Json, original: Dashboard) : Map<string, Json> {
    const byId = new Map<string, Json>();
    const fileScreens = Array.isArray(file.screens) ? file.screens : [];

    for (const [screenIndex, screen] of original.screens.entries()) {
        const fileScreen: unknown = fileScreens[screenIndex];
        const fileTiles: unknown[] = isRecord(fileScreen) && Array.isArray(fileScreen.tiles) ? fileScreen.tiles : [];
        let next = 0;
        const take = (fileTile: unknown) : void => {
            const tile = screen.tiles[next++];
            if (tile && isRecord(fileTile)) {
                byId.set(tile.id, fileTile);
            }
        };
        for (const fileTile of fileTiles) {
            take(fileTile);
            if (isRecord(fileTile) && fileTile.type === 'group' && Array.isArray(fileTile.tiles)) {
                for (const held of fileTile.tiles) {
                    take(held);
                }
            }
        }
    }

    return byId;
}

/** What the file says for a tile: its own entry, the entry it is a copy of, or its new form. */
function fileFormOf(tile: ScreenTile, byId: ReadonlyMap<string, Json>, origins: ReadonlyMap<string, TileOrigin>) : Json {
    const own = byId.get(tile.id);
    if (own) {
        return own;
    }

    const origin = origins.get(tile.id);
    if (origin && 'copyOf' in origin) {
        const original = byId.get(origin.copyOf);
        if (original) {
            return original;
        }
    }
    if (origin && 'json' in origin) {
        return { ...origin.json };
    }

    // Without an origin: as the editor holds it, defaults filled in.
    return tile as unknown as Json;
}

const sameJson = (a: unknown, b: unknown) : boolean => JSON.stringify(a) === JSON.stringify(b);

/** A style as a file writes it (TS-1): what is set, and none at all when nothing is set. */
function writeStyle(written: Json, key: string, style: TileStyle | undefined) : void {
    const set = Object.fromEntries(Object.entries(style ?? {}).filter(([, value]) => value !== undefined));
    if (Object.keys(set).length > 0) {
        written[key] = set;
    } else {
        delete written[key];
    }
}

const isObject = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/** The schema an object value follows: the schema itself, or the variant of its union that is an object. */
/** The object schema of a value: the schema, or of its object variants the one that has all the value's fields (a thermostat's way), else the first. */
function objectSchemaOf(schema: unknown, value: Record<string, unknown>) : Record<string, unknown> | undefined {
    if (!isObject(schema)) {
        return undefined;
    }
    if (isObject(schema.properties)) {
        return schema;
    }

    const variants = (Array.isArray(schema.anyOf) ? schema.anyOf : Array.isArray(schema.oneOf) ? schema.oneOf : []).filter((variant): variant is Record<string, unknown> => isObject(variant) && isObject(variant.properties));

    return variants.find((variant) => Object.keys(value).every((key) => Object.hasOwn(variant.properties as Record<string, unknown>, key))) ?? variants[0];
}

/**
 * A value as a file writes it, without what equals its schema's default, at any depth (§5.7):
 * an edited tile's options stay as short as written by hand. An object left empty by it goes too
 * when its default is empty.
 */
export function withoutDefaults(schema: unknown, value: unknown) : unknown {
    if (Array.isArray(value)) {
        const items = isObject(schema) ? schema.items : undefined;

        return value.map((item) => withoutDefaults(items, item));
    }

    const objectSchema = isObject(value) ? objectSchemaOf(schema, value) : undefined;
    if (!isObject(value) || !objectSchema) {
        return value;
    }

    const properties = objectSchema.properties as Record<string, unknown>;
    const written: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
        const property = properties[key];
        const shorter = withoutDefaults(property, item);
        if (isObject(property) && property.default !== undefined && sameJson(shorter, property.default)) {
            continue;
        }
        written[key] = shorter;
    }

    return written;
}

/** A data source as a file writes it: what was given, `params` left out when empty. */
function writeSource(source: DataSource) : Json {
    const { params, ...rest } = source;

    return Object.keys(params).length > 0 ? { ...rest, params } : rest;
}

/**
 * The edits the editor made to a tile, over its file form: the title and title icon, the data
 * sources (one as the short `dataSource`, E6), the options and the tap, each written only where it differs
 * from what the file form says, so that an unedited tile keeps its file form, short forms and all.
 */
function withEdits(written: Json, tile: ScreenTile) : void {
    const parsed = ScreenTileSchema.safeParse(written);
    if (!parsed.success) {
        return;
    }

    const before = parsed.data;
    for (const key of ['label', 'titleIcon', 'styleName'] as const) {
        if (tile[key] !== before[key]) {
            if (tile[key] === undefined) {
                delete written[key];
            } else {
                written[key] = tile[key];
            }
        }
    }
    if (!sameJson(tile.style ?? {}, before.style ?? {})) {
        writeStyle(written, 'style', tile.style);
    }
    if (tile.type === 'group' || before.type === 'group') {
        return;
    }

    if (!sameJson(tile.dataSources, before.dataSources)) {
        const names = Object.keys(tile.dataSources);
        delete written.dataSource;
        delete written.dataSources;
        if (names.length === 1 && names[0] === PRIMARY_SOURCE) {
            written.dataSource = writeSource(tile.dataSources[PRIMARY_SOURCE]!);
        } else if (names.length > 0) {
            written.dataSources = Object.fromEntries(Object.entries(tile.dataSources).map(([name, source]) => [name, writeSource(source)]));
        }
    }
    if (!sameJson(tile.options, before.options)) {
        written.options = withoutDefaults(tileOptionsJsonSchema(tile.type), tile.options) as Json;
    }
    if (!sameJson(tile.tap ?? {}, before.tap ?? {})) {
        if (tile.tap === undefined) {
            delete written.tap;
        } else {
            written.tap = { ...tile.tap };
        }
    }
}

/**
 * A tile as a file writes it by hand, for a tile that comes from no file (copied in from another
 * dashboard, L-14): the short `dataSource` for one `primary` source, and options without defaults.
 */
export function tileFileForm(tile: ScreenTile) : Json {
    const written: Json = { type: tile.type, id: tile.id };
    for (const key of ['label', 'titleIcon', 'styleName'] as const) {
        if (tile[key] !== undefined) {
            written[key] = tile[key];
        }
    }
    written.layout = { ...tile.layout };
    writeStyle(written, 'style', tile.style);
    if (tile.type === 'group') {
        return written;
    }

    const names = Object.keys(tile.dataSources);
    if (names.length === 1 && names[0] === PRIMARY_SOURCE) {
        written.dataSource = writeSource(tile.dataSources[PRIMARY_SOURCE]!);
    } else if (names.length > 0) {
        written.dataSources = Object.fromEntries(Object.entries(tile.dataSources).map(([name, source]) => [name, writeSource(source)]));
    }
    const options = withoutDefaults(tileOptionsJsonSchema(tile.type), tile.options);
    if (isRecord(options) && Object.keys(options).length > 0) {
        written.options = options;
    }
    if (tile.tap) {
        written.tap = { ...tile.tap };
    }

    return inOrder(written, TILE_KEY_ORDER);
}

/** The tile as the file has it, with its id next to its type, its place and its edits from the editor. */
function writeTile(tile: ScreenTile, byId: ReadonlyMap<string, Json>, origins: ReadonlyMap<string, TileOrigin>) : Json {
    // The id the file has is the original's for a copy: the tile's own id replaces it.
    const { type, id: _fileId, ...rest } = fileFormOf(tile, byId, origins);
    const written: Json = { type, id: tile.id, ...rest, layout: { ...tile.layout } };
    // A group holds no tiles any more; those it held are on the screen now, written there.
    delete written.tiles;
    withEdits(written, tile);

    return written;
}

/**
 * The connections of `edited`, each kept as the file has it where it did not change, so that what
 * the parser does not keep (a key it does not know) stays.
 */
function writeConnections(file: Json, edited: Dashboard) : Json {
    const fileConnections = isRecord(file.connections) ? file.connections : {};
    const written: Json = {};
    for (const [name, connection] of Object.entries(edited.connections)) {
        const own = fileConnections[name];
        const unchanged = isRecord(own) && JSON.stringify({ driver: own.driver, config: own.config }) === JSON.stringify({ driver: connection.driver, config: connection.config });
        written[name] = unchanged ? own : { ...(isRecord(own) ? own : {}), driver: connection.driver, config: connection.config };
    }

    return written;
}

/** The keys a tile is written with first, in this order (the owner, 2026-11-02, S-4a); the others follow as the file has them. */
const TILE_KEY_ORDER = ['id', 'type', 'layout', 'dataSource', 'dataSources', 'label', 'options'];
/** The keys a dashboard is written with first: the version, then the connections. */
const DASHBOARD_KEY_ORDER = ['schemaVersion', 'connections'];

/** `json` with the keys of `order` first, in that order, and the others after them as they were. */
function inOrder(json: Json, order: readonly string[]) : Json {
    const ordered: Json = {};
    for (const key of order) {
        if (Object.hasOwn(json, key)) {
            ordered[key] = json[key];
        }
    }
    for (const [key, value] of Object.entries(json)) {
        if (!Object.hasOwn(ordered, key)) {
            ordered[key] = value;
        }
    }

    return ordered;
}

/** A tile in the order of its keys, and the tiles a group holds (an older file's) likewise. */
function orderTile(tile: unknown) : unknown {
    if (!isRecord(tile)) {
        return tile;
    }

    const ordered = inOrder(tile, TILE_KEY_ORDER);
    if (Array.isArray(ordered.tiles)) {
        ordered.tiles = ordered.tiles.map(orderTile);
    }

    return ordered;
}

/** A dashboard file in the order of its keys, and of its tiles' keys, so that it reads the same by hand however it was edited. */
function orderFile(file: Json) : Json {
    const ordered = inOrder(file, DASHBOARD_KEY_ORDER);
    if (Array.isArray(ordered.screens)) {
        ordered.screens = ordered.screens.map((screen) => (isRecord(screen) && Array.isArray(screen.tiles) ? { ...screen, tiles: screen.tiles.map(orderTile) } : screen));
    }

    return ordered;
}

/** The indentation the file uses, so that the written file looks like the one that was read. */
function indentOf(text: string) : string | number {
    const indent = /\n([ \t]+)"/.exec(text)?.[1];

    return indent ?? 2;
}

/**
 * The dashboard file `text` with the connections, screens and tiles of `edited`, a version of the dashboard that
 * `text` parses to. Every tile is written with its id; tiles the file does not have are written
 * from `origins`. Throws when `text` is not a dashboard file or the result does not validate; the
 * caller then keeps the file as it was.
 */
export function writeDashboard(text: string, edited: Dashboard, origins: ReadonlyMap<string, TileOrigin> = new Map()) : string {
    const parsed = parseDashboard(text);
    const json: unknown = JSON.parse(text);
    const file = isRecord(json) ? migrateDashboard(json) : 'not a JSON object';
    if (!parsed.ok || typeof file === 'string') {
        throw new Error(`The dashboard file cannot be read: ${typeof file === 'string' ? file : parsed.ok ? '' : parsed.issues.join('; ')}`);
    }

    const byId = fileTilesById(file, parsed.dashboard);
    const fileScreens: unknown[] = Array.isArray(file.screens) ? file.screens : [];
    const screens = edited.screens.map((screen, index) => {
        const fileScreen = fileScreens[index];
        const written: Json = { ...(isRecord(fileScreen) ? fileScreen : {}), name: screen.name };
        if (screen.icon === undefined) {
            delete written.icon;
        } else {
            written.icon = screen.icon;
        }
        written.tiles = screen.tiles.map((tile) => writeTile(tile, byId, origins));

        return written;
    });

    const connections = file.connections !== undefined || Object.keys(edited.connections).length > 0 ? { connections: writeConnections(file, edited) } : {};
    const top: Json = { ...file, ...connections };
    if (!sameJson(edited.tileStyle ?? {}, parsed.dashboard.tileStyle ?? {})) {
        writeStyle(top, 'tileStyle', edited.tileStyle);
    }
    // The user's own styles (tile-styles.md TY-9): none left is no list.
    if (!sameJson(edited.tileStyles ?? [], parsed.dashboard.tileStyles ?? [])) {
        if (edited.tileStyles === undefined || edited.tileStyles.length === 0) {
            delete top.tileStyles;
        } else {
            top.tileStyles = edited.tileStyles;
        }
    }
    // Made larger, or its margin and spacing set (issue #35); a grid that did not change stays as the file has it.
    if (!sameJson(edited.grid, parsed.dashboard.grid)) {
        top.grid = { ...edited.grid };
    }
    if (edited.background !== parsed.dashboard.background) {
        if (edited.background === undefined) {
            delete top.background;
        } else {
            top.background = edited.background;
        }
    }
    const result = `${JSON.stringify(orderFile({ ...top, screens }), null, indentOf(text))}${text.endsWith('\n') ? '\n' : ''}`;
    const check = parseDashboard(result);
    if (!check.ok) {
        throw new Error(`The edited dashboard is not valid: ${check.issues.join('; ')}`);
    }

    return result;
}

export type MigrateResult = { ok: true; text: string; from: number; to: number } | { ok: false; issues: string[] };

/**
 * The dashboard file `text` in the current schema version, as the migrations make it, kept in the
 * file's own indentation (the server's admin page, server-admin A-12). A file in the current version
 * comes back as it is. Refused when it is not a dashboard, or would not be a valid one.
 */
export function migrateDashboardText(text: string) : MigrateResult {
    let json: unknown;
    try {
        json = JSON.parse(text);
    } catch (error) {
        return { ok: false, issues: [`not JSON: ${error instanceof Error ? error.message : String(error)}`] };
    }
    if (!isRecord(json)) {
        return { ok: false, issues: ['not a JSON object'] };
    }

    const migrated = migrateDashboard(json);
    if (typeof migrated === 'string') {
        return { ok: false, issues: [migrated] };
    }

    const from = json.schemaVersion as number;
    const result = from === SCHEMA_VERSION ? text : `${JSON.stringify(orderFile(migrated), null, indentOf(text))}${text.endsWith('\n') ? '\n' : ''}`;
    const check = parseDashboard(result);

    return check.ok ? { ok: true, text: result, from, to: SCHEMA_VERSION } : { ok: false, issues: check.issues };
}
